import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rpcClient } from 'src/api/rpc/client';
import type {
  DataAssetResponse,
  DataAssetValidationFieldInput,
  DataAssetWithVersionsResponse,
} from '@erp/rpc';

const DATA_ASSETS_KEY = 'data-assets';

export function useListDataAssets() {
  return useQuery<{ dataAssets: DataAssetResponse[] }>({
    queryKey: [DATA_ASSETS_KEY, 'list'],
    queryFn: () => rpcClient.dataAssets.list({}),
  });
}

export function useGetDataAsset(id: string | undefined) {
  return useQuery<{
    dataAsset: DataAssetWithVersionsResponse;
  }>({
    queryKey: [DATA_ASSETS_KEY, id],
    queryFn: () => {
      if (!id) throw new Error('useGetDataAsset: id is required');
      return rpcClient.dataAssets.get({ id });
    },
    enabled: !!id,
  });
}

export function useCreateDataAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      name: string;
      description?: string;
      fileName: string;
      contentType: string;
    }) => rpcClient.dataAssets.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_ASSETS_KEY] });
    },
  });
}

export function useConfirmDataAssetUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { versionId: string; fileSize?: number }) =>
      rpcClient.dataAssets.confirmUpload(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_ASSETS_KEY] });
    },
  });
}

export function useUpdateDataAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      id: string;
      name?: string;
      description?: string | null;
    }) => rpcClient.dataAssets.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_ASSETS_KEY] });
    },
  });
}

export function useDeleteDataAsset() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { id: string }) => rpcClient.dataAssets.delete(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_ASSETS_KEY] });
    },
  });
}

export function useUploadDataAssetVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      dataAssetId: string;
      fileName: string;
      contentType: string;
      reason?: string;
    }) => rpcClient.dataAssets.uploadVersion(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_ASSETS_KEY] });
    },
  });
}

export function useConfirmVersionUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { versionId: string; fileSize?: number }) =>
      rpcClient.dataAssets.confirmVersionUpload(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_ASSETS_KEY] });
    },
  });
}

export function useRevertDataAssetVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { dataAssetId: string; versionId: string }) =>
      rpcClient.dataAssets.revertVersion(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_ASSETS_KEY] });
    },
  });
}

export function useGetVersionSignedUrl() {
  return useMutation({
    mutationFn: (input: { versionId: string }) =>
      rpcClient.dataAssets.getVersionSignedUrl(input),
  });
}

/**
 * Validates an asset against a schema definition and, unless `dryRun` is set,
 * attaches the schema when every record conforms. A non-conforming asset is a
 * successful call returning `attached: false` plus the report — not an error.
 */
export function useAttachDataAssetSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      dataAssetId: string;
      schemaDefinitionId: string;
      dryRun?: boolean;
    }) => rpcClient.dataAssets.attachSchema(input),
    onSuccess: (result) => {
      if (result.attached) {
        queryClient.invalidateQueries({ queryKey: [DATA_ASSETS_KEY] });
      }
    },
  });
}

export function useDetachDataAssetSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { dataAssetId: string }) =>
      rpcClient.dataAssets.detachSchema(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_ASSETS_KEY] });
    },
  });
}

/**
 * Checks an asset against candidate fields that have not been saved as a schema
 * yet, so a suggestion can be proven against the file before it is kept.
 */
export function useValidateDataAssetFields() {
  return useMutation({
    mutationFn: (input: {
      dataAssetId: string;
      fields: DataAssetValidationFieldInput[];
    }) => rpcClient.dataAssets.validateFields(input),
  });
}
